import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';

const types = [
  ['all', 'All Content'],
  ['news', 'News'],
  ['events', 'Events'],
  ['notices', 'Notices'],
  ['departments', 'Departments'],
  ['downloads', 'Downloads'],
  ['staff', 'Staff'],
  ['journals', 'Journals'],
  ['projects', 'Projects'],
];

export default function AdvancedSearch() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') || '');
  const [type, setType] = useState(params.get('type') || 'all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const grouped = useMemo(() => data?.grouped || {}, [data]);

  async function search(e) {
    e?.preventDefault();
    if (!q.trim()) return;
    setLoading(true);
    setError('');
    try {
      const next = new URLSearchParams({ q: q.trim(), type });
      setParams(next);
      const result = await api(`/api/search/advanced?${next.toString()}`);
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <main id="main-content" className="min-h-screen bg-slate-50 px-4 py-16">
      <section className="mx-auto max-w-7xl">
        <p className="text-sm font-black uppercase tracking-[0.25em] text-emerald-700">Advanced Search</p>
        <h1 className="mt-3 text-4xl font-black text-slate-950 md:text-5xl">Find anything across the FPB website</h1>
        <p className="mt-4 max-w-3xl text-slate-600">Search news, notices, departments, downloads, staff, research publications and institutional projects from one place.</p>

        <form onSubmit={search} className="mt-8 grid gap-3 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-[1fr_230px_auto]">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="rounded-2xl border border-slate-200 px-4 py-4 outline-none focus:border-emerald-600"
            placeholder="Search by keyword, department, author, notice, project..."
            required
          />
          <select value={type} onChange={(e) => setType(e.target.value)} className="rounded-2xl border border-slate-200 px-4 py-4 outline-none focus:border-emerald-600">
            {types.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <button className="rounded-2xl bg-emerald-700 px-8 py-4 font-black text-white hover:bg-emerald-800">
            {loading ? 'Searching...' : 'Search'}
          </button>
        </form>

        {error ? <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4 font-bold text-red-700">{error}</div> : null}

        {data ? (
          <div className="mt-10">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-2xl font-black text-slate-950">{data.total} result{data.total === 1 ? '' : 's'} for “{data.query}”</h2>
              <Link to="/sitemap" className="text-sm font-bold text-emerald-700 hover:underline">View site map</Link>
            </div>

            {data.total === 0 ? (
              <div className="rounded-3xl border border-slate-200 bg-white p-8 text-slate-600 shadow-sm">No matching content was found. Try a shorter keyword or select “All Content”.</div>
            ) : (
              <div className="space-y-8">
                {Object.entries(grouped).map(([group, items]) => (
                  <section key={group} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                    <h3 className="text-xl font-black capitalize text-slate-950">{group.replace('_', ' ')}</h3>
                    <div className="mt-4 grid gap-4 md:grid-cols-2">
                      {items.map((item) => (
                        <Link key={`${item.type}-${item.id}`} to={item.url || '/search'} className="rounded-2xl border border-slate-100 p-5 transition hover:border-emerald-300 hover:bg-emerald-50/50">
                          <div className="text-xs font-black uppercase tracking-[0.18em] text-emerald-700">{item.type}</div>
                          <h4 className="mt-2 text-lg font-black text-slate-950">{item.title}</h4>
                          <p className="mt-2 line-clamp-3 text-sm leading-6 text-slate-600">{item.summary}</p>
                        </Link>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </div>
        ) : null}
      </section>
    </main>
  );
}
